import { Button } from "@bcc/components/ui/button";
import { Card, CardContent } from "@bcc/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@bcc/components/ui/dialog";
import {
  CheckCircle,
  Clock,
  ExternalLink,
  MessageSquare,
  Package,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { setSlaDetailModalTestId } from "./sla-detail-modal.utils";

interface SlaDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  slaData: {
    score: number;
    changePercent: number;
    categoryRatings: {
      fulfillmentSpeed: number;
      orderAccuracy: number;
      responseRate: number;
      onTimeDispatch: number;
    };
  };
  testId?: string;
}

const weeklyTrendData = [
  { day: "Mon", score: 88 },
  { day: "Tue", score: 90 },
  { day: "Wed", score: 89 },
  { day: "Thu", score: 91 },
  { day: "Fri", score: 92 },
  { day: "Sat", score: 93 },
  { day: "Sun", score: 92 },
];

export function SlaDetailModal({
  isOpen,
  onClose,
  slaData,
  testId,
}: SlaDetailModalProps) {
  const { t } = useTranslation();
  const metrics = [
    {
      name: t("dashboardComponents.slaDetailModal.fulfillmentSpeed"),
      value: slaData.categoryRatings.fulfillmentSpeed,
      target: "≤ 48 hrs",
      icon: Clock,
      color: "#0D9488",
    },
    {
      name: t("dashboardComponents.slaDetailModal.onTimeDispatch"),
      value: slaData.categoryRatings.onTimeDispatch,
      target: "≥ 98%",
      icon: Package,
      color: "#3B82F6",
    },
    {
      name: t("dashboardComponents.slaDetailModal.orderAccuracy"),
      value: slaData.categoryRatings.orderAccuracy,
      target: "≥ 99%",
      icon: CheckCircle,
      color: "#22C55E",
    },
    {
      name: t("dashboardComponents.slaDetailModal.responseRate"),
      value: slaData.categoryRatings.responseRate,
      target: "≥ 95%",
      icon: MessageSquare,
      color: "#8B5CF6",
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        testId={testId ?? setSlaDetailModalTestId("content")}
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl">
            {t("dashboardComponents.slaDetailModal.title")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* KPI Metrics Grid */}
          <div className="grid gap-4 md:grid-cols-2">
            {metrics.map((metric) => (
              <Card key={metric.name} className="border-[#E5E7EB]">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-[#6B7280]">
                        {metric.name}
                      </p>
                      <p className="text-2xl font-bold text-[#111827]">
                        {metric.value}%
                      </p>
                      <p className="text-xs text-[#6B7280]">
                        Target: {metric.target}
                      </p>
                    </div>
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-lg"
                      style={{ backgroundColor: metric.color }}
                    >
                      <metric.icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Weekly Trend Chart */}
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#111827]">
                    {t("dashboardComponents.slaDetailModal.weeklyTrend")}
                  </h3>
                  <p className="text-sm text-[#6B7280]">
                    {t("dashboardComponents.slaDetailModal.last7Days")}
                  </p>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={weeklyTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis
                        dataKey="day"
                        stroke="#6B7280"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        stroke="#6B7280"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        domain={[80, 100]}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#FFFFFF",
                          border: "1px solid #E5E7EB",
                          borderRadius: "8px",
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="score"
                        stroke="#0D9488"
                        strokeWidth={3}
                        dot={{ fill: "#0D9488", r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Button */}
          <div className="flex justify-end">
            <Button variant="outline" className="gap-2 bg-transparent">
              {t("dashboardComponents.slaDetailModal.viewFullReport")}
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
